import type {PrimerComponentHero} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentHero'
import type {FeaturedBentoType} from '@github-ui/swp-core/schemas/contentful/contentTypes/featuredBento'
import type {PrimerComponentLabel} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentLabel'
import type {PrimerComponentLogoSuite} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentLogoSuite'
import type {PrimerComponentSectionIntro} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentSectionIntro'
import type {PrimerComponentPricingOptions} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentPricingOptions'
import type {PrimerComponentTestimonial} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentTestimonial'
import type {PrimerCards} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerCards'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import {getSectionContentById, type GenericGroup, type GenericSection} from '../../../../brand/lib/types/contentful'
import type {PlanCardProps} from '../../../../brand/components/PlanCard/PlanCard'
import type {HeroProps} from '../sections/Hero/Hero'
import type {FeaturesProps} from '../sections/Features/Features'
import type {PricingProps} from '../sections/Pricing/Pricing'
import type {ResourcesProps} from '../sections/Resources/Resources'

// Components ---
const sanitizeHeroComponentData = (heroComponent: PrimerComponentHero) => {
  if (!heroComponent?.fields) {
    return null
  }

  const fields = heroComponent.fields

  const intro: HeroProps['intro'] = {
    label: (fields.label as PrimerComponentLabel)?.fields,
    title: fields.heading || '',
    ctaPrimary: {
      href: fields.callToActionPrimary?.fields?.href || '',
      text: fields.callToActionPrimary?.fields?.text || '',
      variant: fields.callToActionPrimaryVariant,
    },
    ctaSecondary: {
      href: fields.callToActionSecondary?.fields?.href || '',
      text: fields.callToActionSecondary?.fields?.text || '',
    },
  }

  return intro
}

const sanitizeBentoGroupData = (bentoGroup: GenericGroup) => {
  if (!bentoGroup?.fields?.content) {
    return []
  }

  const bentos = bentoGroup.fields.content
    .filter(item => item?.sys?.contentType?.sys?.id === 'featuredBento')
    .map(bento => {
      const fields = (bento.fields as FeaturedBentoType['fields']) || {}

      const title = documentToPlainTextString(fields.heading) || ''

      return {
        id: bento.sys.id,
        title,
        description: fields.title,
        icon: fields.icon || '',
        image: {
          urlDesktop: fields.image?.fields?.file?.url || '',
          urlMobile: fields.imageMobile?.fields?.file?.url || '',
          description: fields.image?.fields?.description,
        },
        link: {
          href: fields.link?.fields?.href || '',
          text: fields.link?.fields?.text || '',
        },
      }
    }) as HeroProps['bentos'] | FeaturesProps['bentos']

  return bentos
}

const sanitizeTestimonialData = (testimonialComponent: PrimerComponentTestimonial): FeaturesProps['testimonial'] => {
  if (!testimonialComponent?.fields) {
    return {quote: '', name: '', position: ''}
  }

  const fields = testimonialComponent.fields
  const quote = documentToPlainTextString(fields.quote) || ''
  const name = fields.author?.fields?.fullName || ''
  const position = fields.author?.fields?.position || ''

  return {quote, name, position}
}

const sanitizePricingIntroData = (sectionIntro: PrimerComponentSectionIntro) => {
  const fields = sectionIntro?.fields || {}

  const intro: PricingProps['intro'] = {
    title: documentToPlainTextString(fields.heading) || '',
    description: fields.description ? documentToPlainTextString(fields.description) : '',
    iconUrl: fields.leadingComponent?.fields?.file?.url || '',
    link: {
      href: fields.link?.fields?.href || '',
      text: fields.link?.fields?.text || '',
    },
  }

  return intro
}

const sanitizePricingPlansData = (pricingOptions: PrimerComponentPricingOptions) => {
  if (!pricingOptions?.fields?.items) {
    return []
  }

  const plans: PlanCardProps[] = pricingOptions.fields.items
    .filter(item => item?.sys?.contentType?.sys?.id === 'primerComponentPricingOptionsItem')
    .map((plan): PlanCardProps => {
      const fields = plan.fields || {}

      const priceText = documentToPlainTextString(fields.currentPrice) || '0'
      const priceValue = parseFloat(priceText) || 0

      return {
        id: plan.sys.id,
        label: fields.label?.fields?.text || '',
        title: documentToPlainTextString(fields.heading) || '',
        description: fields.description ? documentToPlainTextString(fields.description) : '',
        price: {
          value: priceValue,
          currency: fields.currencyCode || 'USD',
          currencySymbol: fields.currencySymbol || '$',
          detailText: fields.priceTrailingText || '',
        },
        cta1: {
          label: fields.callToActionPrimary?.fields?.text || '',
          href: fields.callToActionPrimary?.fields?.href || '',
        },
        cta2: {
          label: fields.callToActionSecondary?.fields?.text || '',
          href: fields.callToActionSecondary?.fields?.href || '',
        },
        detailText: fields.footnote ? documentToPlainTextString(fields.footnote) : '',
      }
    })

  return plans
}

const sanitizeResourcesCardsData = (cardsComponent: PrimerCards) => {
  if (!cardsComponent?.fields?.cards) {
    return []
  }

  const cards: ResourcesProps['cards'] = cardsComponent.fields.cards
    .filter(item => item?.sys?.contentType?.sys?.id === 'primerComponentCard')
    .map(card => {
      const fields = card.fields || {}

      return {
        id: card.sys.id,
        title: fields.heading || '',
        description: fields.description ? documentToPlainTextString(fields.description) : '',
        icon: fields.icon as ResourcesProps['cards'][number]['icon'],
        link: {
          href: fields.href || '',
          label: fields.ctaText || '',
        },
      }
    })

  return cards
}

// Sections ---
export const sanitizeHeroSectionData = (heroSectionData: GenericSection) => {
  const heroComponent = heroSectionData?.fields.content?.find(
    item => item.sys.contentType.sys.id === 'primerComponentHero',
  ) as PrimerComponentHero

  const bentoGroupComponent = getSectionContentById({
    content: heroSectionData?.fields.content,
    id: 'securityAdvancedSecurityHeroBento',
  }) as GenericGroup

  const logoSuiteComponent = heroSectionData?.fields.content?.find(
    item => item?.sys?.contentType?.sys?.id === 'primerComponentLogoSuite',
  ) as PrimerComponentLogoSuite | undefined

  const intro = sanitizeHeroComponentData(heroComponent)
  const bentos = sanitizeBentoGroupData(bentoGroupComponent)

  const data = {
    intro,
    bentos,
    logoSuiteComponent,
  } as Omit<HeroProps, 'intro'> & {intro?: HeroProps['intro']}

  return data
}

export const sanitizeFeaturesSectionData = (featuresSectionData: GenericSection) => {
  const intro = featuresSectionData?.fields?.sectionIntro?.fields
  const title = intro?.heading ? documentToPlainTextString(intro.heading) : ''
  const description = intro?.description ? documentToPlainTextString(intro?.description) : ''

  const testimonialComponent = featuresSectionData.fields.content.find(
    item => item?.sys?.contentType?.sys?.id === 'primerComponentTestimonial',
  ) as PrimerComponentTestimonial
  const testimonial = sanitizeTestimonialData(testimonialComponent)

  const bentoGroupComponent = getSectionContentById({
    content: featuresSectionData?.fields.content,
    id: 'securityAdvancedSecurityFeaturesBento',
  }) as GenericGroup
  const bentos = sanitizeBentoGroupData(bentoGroupComponent)

  const data = {
    title,
    description,
    bentos,
    testimonial,
  } as FeaturesProps

  return data
}

export const sanitizePricingSectionData = (pricingSectionData: GenericSection) => {
  const fields = pricingSectionData?.fields
  if (!fields?.sectionIntro || !fields?.content) {
    return null
  }

  const intro = sanitizePricingIntroData(fields.sectionIntro)

  const pricingOptionsComponent = fields.content.find(
    item => item?.sys?.contentType?.sys?.id === 'primerComponentPricingOptions',
  ) as PrimerComponentPricingOptions
  const plans = sanitizePricingPlansData(pricingOptionsComponent)

  const data = {intro, plans} as PricingProps
  return data
}

export const sanitizeResourcesSectionData = (resourcesSectionData: GenericSection) => {
  const fields = resourcesSectionData?.fields
  if (!fields?.sectionIntro || !fields?.content) {
    return null
  }

  const title = fields.sectionIntro.fields?.heading
    ? documentToPlainTextString(fields.sectionIntro.fields?.heading)
    : ''

  const cardsComponent = fields.content.find(item => item?.sys?.contentType?.sys?.id === 'primerCards') as PrimerCards
  const cards = sanitizeResourcesCardsData(cardsComponent)

  return {
    title,
    cards,
  }
}
